import React, { useState, useEffect } from 'react';
import { getStoredToken } from '../services/authSession';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import {
  Building2, CheckCircle, Layers, DollarSign, Settings,
  ArrowLeft, RefreshCw, FileText, PieChart, Plus
} from 'lucide-react';

import API from '../services/apiBase';

const ALLOCATION_TYPE_LABELS = {
  facility: 'Facility',
  hr: 'HR',
  it: 'IT',
  service_center: 'Service Center',
};

const ClientConfigView = ({ clientId, onBack }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [client, setClient] = useState(null);
  const [intake, setIntake] = useState(null);
  const [addingAllocation, setAddingAllocation] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const token = getStoredToken();

  useEffect(() => {
    loadClientData();
  }, [clientId]);

  const loadClientData = async () => {
    setLoading(true);
    setError('');

    try {
      const clientRes = await fetch(`${API}/clients/${clientId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (clientRes.ok) setClient(await clientRes.json());

      const intakeRes = await fetch(`${API}/intake/v2/list?client_id=${clientId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (intakeRes.ok) {
        const intakes = await intakeRes.json();
        const clientIntake = intakes.find(i => i.client_id === clientId);
        if (clientIntake) setIntake(clientIntake);
      }
    } catch (err) {
      setError('Failed to load client data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const availableTypes = Object.keys(ALLOCATION_TYPE_LABELS).filter(
    t => !(intake?.allocation_types_enabled || []).includes(t)
  );

  const handleAddAllocation = async () => {
    if (!selectedType || !intake?.id) return;
    setSaving(true);
    setSaveError('');

    const existingSections = intake.allocation_sections || [];
    const maxOrder = existingSections.reduce((m, s) => Math.max(m, s.display_order ?? 0), -1);
    const newSection = {
      id: `alloc_${Math.random().toString(36).slice(2, 10)}`,
      name: ALLOCATION_TYPE_LABELS[selectedType],
      key: `alloc_${selectedType}`,
      allocation_type: selectedType,
      pool_rule: '',
      pool_rule_structured: null,
      objectives: [],
      uses_sqft: false,
      sqft_data_uploaded: false,
      sqft_file_id: null,
      display_order: maxOrder + 1,
      enabled: true,
    };

    try {
      const res = await fetch(`${API}/intake/v2/${intake.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          allocation_types_enabled: [...(intake.allocation_types_enabled || []), selectedType],
          allocation_sections: [...existingSections, newSection],
        }),
      });

      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail?.detail || `HTTP ${res.status}`);
      }

      setAddingAllocation(false);
      setSelectedType('');
      await loadClientData();
    } catch (err) {
      setSaveError('Failed to add allocation: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-600 mr-2" />
        <span>Loading client configuration...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert className="m-4 bg-red-50 border-red-200">
        <AlertDescription className="text-red-800">{error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Clients
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{client?.name || 'Client Configuration'}</h1>
            <p className="text-gray-500">Client ID: {clientId}</p>
          </div>
        </div>
        <Badge className="bg-green-100 text-green-800">
          <CheckCircle className="w-4 h-4 mr-1" />
          Active
        </Badge>
      </div>

      {/* Client Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Company Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-sm text-gray-500">Company Name</span>
              <p className="font-medium">{intake?.company_name || client?.name}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500">Fiscal Year End</span>
              <p className="font-medium">{intake?.fiscal_year_end || '12/31'}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500">Contact Email</span>
              <p className="font-medium">{client?.contact_email || 'N/A'}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500">Complexity Level</span>
              <p className="font-medium capitalize">{intake?.complexity_level || 'Simple'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pool Configuration */}
      {intake && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Fringe Pools */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Layers className="w-5 h-5 text-blue-600" />
                  Fringe Pools
                </CardTitle>
                <CardDescription>{intake.fringe_pools?.length || 0} configured</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {intake.fringe_pools?.map((pool, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <span className="font-medium">{pool.name}</span>
                      {pool.enabled !== false && (
                        <Badge variant="outline" className="text-green-600">Active</Badge>
                      )}
                    </div>
                  )) || <p className="text-gray-500 text-sm">No fringe pools configured</p>}
                </div>
              </CardContent>
            </Card>

            {/* OH Pools */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-blue-600" />
                  OH Divisions
                </CardTitle>
                <CardDescription>{intake.oh_pools?.length || 0} configured</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {intake.oh_pools?.map((pool, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <span className="font-medium text-sm">{pool.name}</span>
                      {pool.enabled !== false && (
                        <Badge variant="outline" className="text-green-600 text-xs">Active</Badge>
                      )}
                    </div>
                  )) || <p className="text-gray-500 text-sm">No OH divisions configured</p>}
                </div>
              </CardContent>
            </Card>

            {/* G&A Pool */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-600" />
                  G&A Pool
                </CardTitle>
                <CardDescription>General & Administrative</CardDescription>
              </CardHeader>
              <CardContent>
                {intake.ga_pool ? (
                  <div className="p-2 bg-gray-50 rounded">
                    <span className="font-medium">{intake.ga_pool.name || 'G&A'}</span>
                    <Badge variant="outline" className="ml-2 text-green-600">Active</Badge>
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">G&A pool not configured</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Allocations */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Allocation Sections
                </CardTitle>
                {availableTypes.length > 0 && !addingAllocation && (
                  <Button size="sm" variant="outline" onClick={() => setAddingAllocation(true)}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add Allocation
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {intake.allocation_sections?.length > 0 ? (
                <div className="grid grid-cols-3 gap-4">
                  {intake.allocation_sections.map((alloc, idx) => (
                    <div key={idx} className="p-3 bg-gray-50 rounded">
                      <span className="font-medium">{alloc.name}</span>
                      <p className="text-sm text-gray-500 capitalize">{alloc.allocation_type}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No allocation sections configured</p>
              )}

              {addingAllocation && (
                <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
                  <p className="text-sm font-medium">Select allocation type to add:</p>
                  <div className="flex flex-wrap gap-2">
                    {availableTypes.map(t => (
                      <button
                        key={t}
                        onClick={() => setSelectedType(t)}
                        className={`px-3 py-1.5 rounded border text-sm font-medium transition-colors ${
                          selectedType === t
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                        }`}
                      >
                        {ALLOCATION_TYPE_LABELS[t]}
                      </button>
                    ))}
                  </div>
                  {saveError && (
                    <p className="text-sm text-red-600">{saveError}</p>
                  )}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={handleAddAllocation}
                      disabled={!selectedType || saving}
                    >
                      {saving ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : null}
                      {saving ? 'Saving...' : 'Add'}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => { setAddingAllocation(false); setSelectedType(''); setSaveError(''); }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Uploaded Files */}
          {intake.uploaded_files?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Uploaded Files
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {intake.uploaded_files.map((file, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div>
                        <span className="font-medium">{file.filename}</span>
                        <span className="text-sm text-gray-500 ml-2">({file.file_type})</span>
                      </div>
                      <span className="text-sm text-gray-500">
                        {(file.size_bytes / 1024).toFixed(1)} KB
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Next Steps</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <Button onClick={() => window.location.href = '/gl'}>
              <FileText className="w-4 h-4 mr-2" />
              Upload GL Data
            </Button>
            <Button variant="outline" onClick={() => window.location.href = '/gl-calculations'}>
              Run Calculations
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientConfigView;
